import * as Cesium from '@cesiumgs/cesium-analytics'
import * as turf from '@turf/turf'
import { convertCoordinates } from '@/components/Cesium/Prj4/Prj4'

function getCenterPosition(viewer: any) {
  const centerResult = viewer.camera.pickEllipsoid(new Cesium.Cartesian2(viewer.canvas.clientWidth / 2, viewer.canvas.clientHeight / 2))
  const curPosition = Cesium.Ellipsoid.WGS84.cartesianToCartographic(centerResult)
  const curLongitude = (curPosition.longitude * 180) / Math.PI
  const curLatitude = (curPosition.latitude * 180) / Math.PI
  return {
    lng: curLongitude,
    lat: curLatitude,
  }
}

function worldToLngLat(viewer: Cesium.Viewer, postions: any) {
  const ellipsoid = viewer.scene.globe.ellipsoid
  const lnglatArr: any = []
  postions.forEach((item: any) => {
    const cartographic = ellipsoid.cartesianToCartographic(item)
    const lat = Cesium.Math.toDegrees(cartographic.latitude)
    const lng = Cesium.Math.toDegrees(cartographic.longitude)
    lnglatArr.push([lng, lat])
  })
  return lnglatArr
}

function coordinateSystemConversion(from: string, to: string, arr: any) {
  const resArr: any = []
  arr.forEach((item: any) => {
    const wgs84Coordinates = convertCoordinates(from, to, item)
    resArr.push([wgs84Coordinates.x, wgs84Coordinates.y])
  })
  return resArr
}

function epsg4326ToCartesian3(viewer: any, arr: Array<Cesium.Cartesian3>) {
  const cartesian3Arr: any = []
  arr.forEach((item: any) => {
    const cartographic = Cesium.Cartographic.fromDegrees(item[0], item[1])
    const cartesian3 = viewer.scene.globe.ellipsoid.cartographicToCartesian(cartographic)
    cartesian3Arr.push(cartesian3)
  })
  return cartesian3Arr
}

function generateRandomRgb() {
  const randomColorComponent = () => Math.floor(Math.random() * 200)

  const color = [randomColorComponent(), randomColorComponent(), randomColorComponent()]

  const colorDifference = 100000
  color.forEach((component, index) => {
    const adjustment = Math.floor(Math.random() * colorDifference) * (Math.random() < 0.5 ? -1 : 1)
    color[index] = Math.max(0, Math.min(255, component + adjustment))
  })

  return color
}

function hexToRgb(hex: string) {
  hex = hex.replace(/^#/, '')

  const bigint = Number.parseInt(hex, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255

  return {
    r: `${r}`,
    g: `${g}`,
    b: `${b}`,
  }
}

function debounce(fn: any, delay: number) {
  let timer: any = null
  return (...args: any) => {
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      fn(...args)
    }, delay)
  }
}

/**
 * 대상 타일의 최대/최소 위경도 좌표 추출
 * @param root 타일
 * @param points 최소/최대 위경도 좌표
 * @returns //
 */
function getChildPoint(root: Cesium.Cesium3DTile, points: any[]) {
  // console.log('root---', root, root.children)
  if (root.children.length === 0)
    return
  root.children.forEach((tile: Cesium.Cesium3DTile, idx: number) => {
    if (tile.children.length > 0) {
      getChildPoint(tile, points)
    }
    // tile.content.getFeature()
    if (tile.content !== undefined) {
      for (let i = 0; i < tile.content.featuresLength; i++) {
        const feature = tile.content.getFeature(i)
        const names = feature.getPropertyNames()
        // console.log('tile----', feature.getProperty('NF_ID'), feature.getProperty('Latitude'), feature.getProperty('Longitude'))
        points[0][0] = Math.max(feature.getProperty('Latitude'), points[0][0]) // 최대 위도
        points[0][1] = Math.max(feature.getProperty('Longitude'), points[0][1]) // 최대 경도
        points[1][0] = Math.min(feature.getProperty('Latitude'), points[1][0]) // 최소 위도
        points[1][1] = Math.min(feature.getProperty('Longitude'), points[1][1]) // 취소 경도
      }
    }
  })
}

/**
 * 체크 영역에 포함된 lod1 건물 숨김
 * @param root 타일
 * @param poly 체크 영역
 * @returns //
 */
function checkChild(root: Cesium.Cesium3DTile, poly: any) {
  // console.log('root---', root, root.children)
  if (root.children.length === 0)
    return
  root.children.forEach((tile: Cesium.Cesium3DTile, idx: number) => {
    if (tile.children.length > 0) {
      checkChild(tile, poly)
    }
    // tile.content.getFeature()
    if (tile.content !== undefined) {
      for (let i = 0; i < tile.content.featuresLength; i++) {
        const feature = tile.content.getFeature(i)
        const names = feature.getPropertyNames()
        // console.log('tile----', feature.getProperty('NF_ID'), feature.getProperty('Latitude'), feature.getProperty('Longitude'))
        const point = turf.point([feature.getProperty('Latitude'), feature.getProperty('Longitude')])
        if (feature.getProperty('NF_ID') !== undefined && turf.booleanContains(poly, point)) {
          feature.show = false
        }
      }
    }
  })
}

/**
 * lod1이 있는 경우, 지금 추가한 tileset과 겹치는 lod1의 모델 숨김 처리하는 함수
 * @param viewer cesium viewer
 * @param tileset 지금 추가한 tileset
 */
function checkOverlab_3dtiles(viewer: Cesium.Viewer, mode: string) {
  // tileset 카운트. tileset외에 추가된 객체는 제외
  let countTileset: number = 0
  let hasLod1: boolean = false
  let tilesetLod1: Cesium.Cesium3DTileset | null = null
  let tilesetFacility: Cesium.Cesium3DTileset | null = null
  for (let i = 0; i < viewer.scene.primitives.length; i++) {
    const tileset: Cesium.Cesium3DTileset = viewer.scene.primitives.get(i)
    if (tileset.basePath !== undefined) {
      countTileset++
      if (tileset.basePath.includes('/static/tiles/lod10729')) {
        // lod1 tileset
        tilesetLod1 = tileset
        hasLod1 = true
      }
      else {
        // lod1 이외의 tileset. 제일 마지막에 추가한 것을 기준으로 사용
        tilesetFacility = tileset
      }
    }
    // console.log('tileset=', tileset.basePath, tileset)
  }
  // console.log('step001=', mode)
  // primitive에 lod1이 있으면서 1개 이상인 경우, 검사
  if (countTileset === 1 || hasLod1 === false)
    return
  // console.log('tileset=', tileset, 'tilesetfa=', tilesetFacility)
  const points: any[] = [[0, 0], [50, 150]]
  getChildPoint(tilesetFacility!.root, points)
  points.forEach((pt: any, index: number) => {
    // console.log('step pt=', pt)
  })
  // console.log('step002')
  if (points[0][0] !== 0 && points[0][1] !== 0 && points[1][0] !== 50 && points[1][1] !== 150) {
    // console.log('step pt2=', points)
    const line = turf.lineString([points[0], points[1]])
    const bbox = turf.bbox(line)
    const poly = turf.bboxPolygon(bbox)
    checkChild(tilesetLod1!.root, poly)
    // console.log('step003')
  }
}

export {
  getCenterPosition,
  worldToLngLat,
  coordinateSystemConversion,
  epsg4326ToCartesian3,
  generateRandomRgb,
  hexToRgb,
  debounce,
  checkOverlab_3dtiles,
}
